import { useCallback, useEffect, useRef, useState } from 'react';
import type { AudioDeviceOption } from './audioDevices.ts';
import { DictationStream } from './dictationStream.ts';
import { openChosenMicrophone } from './openChosenMicrophone.ts';
import { isMicrophoneBlocked } from './roleplayCall.ts';
import { reportError } from './reportError.ts';
import { useDictationMicrophones } from './useDictationMicrophones.ts';
import { voiceMicrophoneConstraints } from './voiceMicrophoneConstraints.ts';

/** What the composer is doing with the microphone right now. */
export type DictationState = 'idle' | 'recording' | 'sending';

/**
 * Why nothing was said in the end. Three different sentences, because there
 * are three different things to do about them: give Wundo the microphone, say
 * it again, or type it.
 */
export type DictationProblem = 'blocked' | 'empty' | 'failed';

export interface Dictation {
  /** Whether this browser can stream the microphone to the server. */
  supported: boolean;
  state: DictationState;
  /** Seconds recorded so far, frozen at what was said once it is sent. */
  seconds: number;
  start: () => void;
  /** Stop and wait for the words. */
  stop: () => void;
  /** Stop and throw the recording away. */
  cancel: () => void;
  /**
   * What can be recorded with. Empty until the microphone has been granted: a
   * browser names the devices it has only to a page it has already trusted
   * with one, and an unnamed list is not a choice anybody can make.
   */
  microphones: AudioDeviceOption[];
  microphoneId: string | null;
  chooseMicrophone: (deviceId: string) => void;
  /**
   * Ask for the microphone so the devices can be named — for the moment the
   * list is about to be read rather than for the moment it is recorded with.
   * Does nothing once the names are in, or while a recording is running.
   */
  requestMicrophones: () => void;
}

export interface DictationRequest {
  /** What was said, trimmed and never empty. */
  onText: (text: string) => void;
  onProblem: (problem: DictationProblem) => void;
}

/* Nobody dictates a chat message for two minutes. At two, it stops itself. */
const CAP_SECONDS = 120;

function canDictate(): boolean {
  return (
    typeof AudioWorkletNode !== 'undefined' &&
    typeof WebSocket !== 'undefined' &&
    typeof navigator !== 'undefined' &&
    navigator.mediaDevices !== undefined
  );
}

/**
 * Speaking a chat message instead of typing it.
 *
 * One dictation at a time, streamed to the server while it is spoken, so the
 * words are ready the moment it is stopped. Nothing is kept: the audio is
 * heard and dropped, here and on the server.
 *
 * Stopping sends. There is no draft in between, so the recording has exactly
 * two ends: the words, or the bin.
 */
export function useDictation({ onText, onProblem }: DictationRequest): Dictation {
  /* The dictation being spoken or waited on. */
  const session = useRef<DictationStream | null>(null);
  /*
   * Which recording is the current one. Opening a microphone takes a moment,
   * and a recording can be ended inside that moment — cancelled before the
   * microphone was ever handed over. Stopping bumps this, so a stream that
   * arrives afterwards is closed on arrival instead of being left open with
   * nobody watching it.
   */
  const generation = useRef(0);
  /*
   * The two answers, read at the moment they are needed rather than closed
   * over, so that a caller who hands over a fresh callback every render does
   * not hand over a fresh `start` and `stop` with it.
   */
  const answers = useRef({ onText, onProblem });

  useEffect(() => {
    answers.current = { onText, onProblem };
  }, [onText, onProblem]);

  const [state, setState] = useState<DictationState>('idle');
  const [seconds, setSeconds] = useState(0);

  const onBlocked = useCallback(() => answers.current.onProblem('blocked'), []);
  const devices = useDictationMicrophones(onBlocked);
  const { chosen, refresh, request } = devices;

  /* Not while a recording is running: it already has the microphone. */
  const requestMicrophones = useCallback(() => {
    if (session.current === null) {
      request();
    }
  }, [request]);

  const start = useCallback(() => {
    if (session.current !== null || !canDictate()) {
      return;
    }

    const mine = generation.current;
    const dictation = new DictationStream();

    void (async () => {
      try {
        const stream = await openChosenMicrophone(
          chosen.current,
          voiceMicrophoneConstraints,
        );

        if (generation.current !== mine) {
          stream.getTracks().forEach((track) => track.stop());
          dictation.cancel();
          return;
        }

        await dictation.listen(stream);
        if (generation.current !== mine) {
          dictation.cancel();
          return;
        }

        session.current = dictation;
        /*
         * Back to nought here rather than where the clock is wound, so the
         * reset belongs to the recording that asked for it.
         */
        setSeconds(0);
        setState('recording');

        /*
         * A recording names the devices too, for anybody who pressed the
         * microphone without ever opening the menu behind it.
         */
        void refresh();
      } catch (caught) {
        dictation.cancel();

        if (isMicrophoneBlocked(caught)) {
          answers.current.onProblem('blocked');
          return;
        }

        reportError(caught, { what: 'chat-dictation-start' });
        answers.current.onProblem('failed');
        return;
      }

      /* Stopped by the user, or by the server at its own cap. */
      const ours = () => session.current === dictation;
      dictation.text.then(
        (said) => {
          if (!ours()) {
            return;
          }

          session.current = null;
          setState('idle');
          if (said === '') {
            answers.current.onProblem('empty');
          } else {
            answers.current.onText(said);
          }
        },
        (caught: unknown) => {
          if (!ours()) {
            return;
          }

          session.current = null;
          setState('idle');
          reportError(caught, { what: 'chat-dictation' });
          answers.current.onProblem('failed');
        },
      );
    })();
  }, [chosen, refresh]);

  const stop = useCallback(() => {
    const current = session.current;
    if (current === null) {
      /*
       * The microphone had not been handed over yet, so there is nothing to
       * send — a recording ended while the browser was still opening it.
       */
      generation.current += 1;
      setState('idle');
      return;
    }

    setState('sending');
    current.stop();
  }, []);

  const cancel = useCallback(() => {
    generation.current += 1;
    const current = session.current;
    session.current = null;
    setState('idle');

    current?.cancel();
  }, []);

  /* The clock on the row, and nothing else: one tick a second while recording. */
  useEffect(() => {
    if (state !== 'recording') {
      return;
    }

    const began = Date.now();
    const timer = setInterval(
      () => setSeconds(Math.floor((Date.now() - began) / 1000)),
      1000,
    );

    return () => clearInterval(timer);
  }, [state]);

  /* And the cap, read off the same clock. */
  useEffect(() => {
    if (state === 'recording' && seconds >= CAP_SECONDS) {
      stop();
    }
  }, [state, seconds, stop]);

  /*
   * Leaving the page lets go of the microphone. A recording light that stays
   * on after the drawer is gone is worse than a sentence that has to be said
   * again, and there is nobody left to send the words to.
   */
  useEffect(
    () => () => {
      generation.current += 1;
      const current = session.current;
      session.current = null;
      current?.cancel();
    },
    [],
  );

  return {
    supported: canDictate(),
    state,
    seconds,
    start,
    stop,
    cancel,
    microphones: devices.microphones,
    microphoneId: devices.microphoneId,
    chooseMicrophone: devices.choose,
    requestMicrophones,
  };
}
